import React, { useState, useEffect, useCallback } from "react";
import {
  FaChartLine,
  FaBoxOpen,
  FaUsers,
  FaUserPlus,
  FaUserEdit,
  FaUserTimes,
  FaBuilding,
  FaList,
  FaCloudUploadAlt,
  FaBox,
  FaTasks,
  FaTrashAlt,
  FaClipboardCheck,
  FaShoppingCart,
  FaArrowRight,
  FaBolt,
  FaRegClock,
  FaFileInvoice,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import * as dashboardService from "../services/dashboard.service";

const ACTIVITY_PAGE_SIZE = 10;

// Icon + accent color per activity type, so each feed row reads at a glance.
const ACTIVITY_STYLE = {
  client_created: { icon: FaUserPlus, color: "success" },
  client_updated: { icon: FaUserEdit, color: "info" },
  client_deleted: { icon: FaUserTimes, color: "danger" },
  product_created: { icon: FaBox, color: "success" },
  product_updated: { icon: FaBox, color: "info" },
  product_deleted: { icon: FaTrashAlt, color: "danger" },
  order_created: { icon: FaShoppingCart, color: "success" },
  order_updated: { icon: FaShoppingCart, color: "info" },
  order_authorization_changed: { icon: FaClipboardCheck, color: "primary" },
  client_catalog_authorization_changed: { icon: FaClipboardCheck, color: "primary" },
  client_catalog_auto_unapproved: { icon: FaExclamationTriangle, color: "warning" },
  tear_sheet_documents_uploaded: { icon: FaCloudUploadAlt, color: "warning" },
  proforma_invoice_generated: { icon: FaFileInvoice, color: "warning" },
  document_uploaded: { icon: FaCloudUploadAlt, color: "warning" },
};

const activityStyle = (action) =>
  ACTIVITY_STYLE[action] || { icon: FaTasks, color: "secondary" };

const formatWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const DashboardHome = () => {
  const { user } = useAuth();
  const now = new Date();
  const currentHour = now.getHours();
  const greeting =
    currentHour < 12
      ? "Good Morning"
      : currentHour < 18
        ? "Good Afternoon"
        : "Good Evening";
  const todayLabel = now.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const [stats, setStats] = useState({
    totalClients: 0,
    totalProducts: 0,
    pendingProducts: 0,
    pendingOrders: 0,
  });
  const [activities, setActivities] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await dashboardService.getDashboardStats();
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled)
          setStats({
            totalClients: 0,
            totalProducts: 0,
            pendingProducts: 0,
            pendingOrders: 0,
          });
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch one page of activity; page 1 replaces the feed, later pages append ("View more").
  const loadActivities = useCallback(async (page) => {
    const { data, pagination } = await dashboardService.getRecentActivities({
      page,
      perPage: ACTIVITY_PAGE_SIZE,
    });
    setActivities((prev) => (page === 1 ? data : [...prev, ...data]));
    setActivityPage(pagination.page);
    setActivityHasMore(pagination.page < pagination.last_page);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) await loadActivities(1);
      } catch {
        if (!cancelled) setActivities([]);
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadActivities]);

  const handleViewMore = async () => {
    setLoadingMore(true);
    try {
      await loadActivities(activityPage + 1);
    } catch {
      // keep the already-loaded rows visible on failure
    } finally {
      setLoadingMore(false);
    }
  };

  const statCards = [
    {
      title: "Total Clients",
      value: stats.totalClients,
      icon: FaUsers,
      color: "primary",
    },
    {
      title: "Total Products",
      value: stats.totalProducts,
      icon: FaBox,
      color: "info",
    },
    {
      title: "Product Approval Pending",
      value: stats.pendingProducts,
      icon: FaClipboardCheck,
      color: "warning",
    },
    {
      title: "Order Approval Pending",
      value: stats.pendingOrders,
      icon: FaShoppingCart,
      color: "danger",
    },
  ];

  const quickActions = [
    {
      icon: FaBoxOpen,
      label: "Add Product",
      path: "/dashboard/product/add-product",
      color: "primary",
      description: "Create new product",
    },
    {
      icon: FaUsers,
      label: "Add Client",
      path: "/dashboard/crm-master/add-client",
      color: "info",
      description: "Register new client",
    },
    {
      icon: FaList,
      label: "Client List",
      path: "/dashboard/crm-master/client-list",
      color: "success",
      description: "View all clients",
    },
    {
      icon: FaChartLine,
      label: "Catalogue",
      path: "/dashboard/product/catalog",
      color: "warning",
      description: "Product catalogue",
    },
    {
      icon: FaBox,
      label: "Client Catalogue",
      path: "/dashboard/product/client-catalog",
      color: "danger",
      description: "Client-specific view",
    },
    {
      icon: FaBuilding,
      label: "Company Details",
      path: "/dashboard/company-details",
      color: "secondary",
      description: "Company info",
    },
    {
      icon: FaCloudUploadAlt,
      label: "DMS Library",
      path: "/dashboard/dms",
      color: "dark",
      description: "Document management",
    },
    {
      icon: FaClipboardCheck,
      label: "Product Authorization",
      path: "/dashboard/product/authorize",
      color: "primary",
      description: "Approve catalog items",
    },
    {
      icon: FaShoppingCart,
      label: "Order Creation",
      path: "/dashboard/order/create",
      color: "success",
      description: "Create new order",
    },
  ];

  const StatCard = ({ title, value, icon: Icon, color, delay }) => (
    <div className="col-lg-3 col-md-6">
      <div
        className={`stat-card-modern dash-accent-${color}`}
        style={{ animationDelay: `${delay}s` }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="stat-card-info-block">
            <p className="stat-card-title">{title}</p>
            <h3 className="stat-card-value">
              {overviewLoading ? (
                <span className="stat-card-skeleton" />
              ) : (
                value
              )}
            </h3>
          </div>
          <div className="stat-card-icon">
            <Icon />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="dashboard-home">
      {/* Hero */}
      <div className="welcome-hero mb-5">
        <span className="welcome-hero-orb welcome-hero-orb-1" />
        <span className="welcome-hero-orb welcome-hero-orb-2" />
        <div className="welcome-hero-content d-flex align-items-center justify-content-between">
          <div>
            <span className="welcome-hero-chip">
              <FaRegClock className="me-2" size={12} />
              {todayLabel}
            </span>
            <div className="welcome-hero-wave">👋</div>
            <h1 className="welcome-hero-title">
              {greeting}, {user?.name}!
            </h1>
            <p className="welcome-hero-sub">
              Welcome back to your CRM Dashboard. Let's make today productive!
            </p>
          </div>
          <div className="welcome-hero-emoji d-none d-md-block">📊</div>
        </div>
      </div>

      {/* Overview */}
      <div className="mb-5">
        <div className="d-flex align-items-center gap-2 mb-4">
          <span className="dash-section-icon">
            <FaChartLine />
          </span>
          <h5 className="dash-section-title mb-0">Dashboard Overview</h5>
        </div>
        <div className="row g-3">
          {statCards.map((card, index) => (
            <StatCard key={card.title} {...card} delay={index * 0.08} />
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-5">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-2">
            <span className="dash-section-icon">
              <FaBolt />
            </span>
            <h5 className="dash-section-title mb-0">Quick Actions</h5>
          </div>
          <small className="text-muted">Access key features instantly</small>
        </div>
        <div className="row g-3">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <div key={action.path} className="col-lg-4 col-md-6">
                <Link
                  to={action.path}
                  className="quick-action-card text-decoration-none"
                >
                  <div
                    className={`quick-action-modern dash-accent-${action.color}`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="quick-action-icon">
                      <Icon size={20} />
                    </div>
                    <div className="quick-action-body">
                      <h6 className="quick-action-title">{action.label}</h6>
                      <p className="quick-action-desc">{action.description}</p>
                    </div>
                    <span className="quick-action-arrow">
                      <FaArrowRight />
                    </span>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <div className="d-flex align-items-center gap-2 mb-4">
          <span className="dash-section-icon">
            <FaRegClock />
          </span>
          <h5 className="dash-section-title mb-0">Recent Activity</h5>
        </div>
        <div className="activity-card">
          <div className="card-body">
            {activityLoading && (
              <p className="text-muted small mb-0">Loading activity…</p>
            )}
            {!activityLoading && activities.length === 0 && (
              <div className="activity-empty">
                <FaRegClock className="activity-empty-icon" />
                <p className="text-muted small mb-0">
                  No recent activity yet. Actions such as adding clients,
                  products, or DMS uploads will appear here.
                </p>
              </div>
            )}
            {!activityLoading &&
              activities.map((item, index) => {
                const { icon: Icon, color } = activityStyle(item.action);
                const isLast = index === activities.length - 1;
                return (
                  <div
                    key={item.id ?? index}
                    className={`activity-row d-flex align-items-center ${!isLast ? "activity-row-divider" : ""}`}
                  >
                    <div className={`activity-dot dash-accent-${color}`}>
                      <Icon />
                    </div>
                    <div className="flex-grow-1">
                      <p className="mb-0 text-dark">
                        <strong>{item.title}</strong>
                        {item.description && (
                          <span className="text-muted">
                            {" "}
                            — {item.description}
                          </span>
                        )}
                      </p>
                      <small className="text-muted">
                        {formatWhen(item.at)}
                        {item.userName ? ` · ${item.userName}` : ""}
                      </small>
                    </div>
                  </div>
                );
              })}

            {!activityLoading && activityHasMore && (
              <div className="activity-more">
                <button
                  type="button"
                  className="activity-more-btn"
                  onClick={handleViewMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    "Loading…"
                  ) : (
                    <>
                      View more <FaArrowRight size={11} className="ms-1" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
